import React, { memo, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEffectOnMount } from 'shared/hooks/useEffectOnMount';
import { fetchHasPremium } from 'shared/api/fetchPremiumData';
import { fetchProfileData, isAuthorizedProfile, ProfileData } from 'shared/api/fetchProfileData';
import { fetchPaymentData } from 'shared/api/fetchPaymentData';
import { UCHIRU_LINK } from 'shared/constants';
import { ApiErrorResponseData } from 'shared/types';
import styles from './index.module.scss';
import { MainBannerSection } from './components/sections/MainBanner';
import { AboutSection } from './components/sections/AboutCourse';
import { PhysicalActivitySection } from './components/sections/PhysicalActivity';
import { NutritionSection } from './components/sections/Nutrition';
import { MetabolismSection } from './components/sections/Metabolism';
import { ImmunitySection } from './components/sections/Immunity';
import { CourseContent } from './components/sections/CourseContent';
import { VideoLessons } from './components/sections/VideoLessons';
import { ContextAnatomyProvider } from './context';
import { URL_REG_EXP, TITLE, PRODUCT_NAME } from './constants';
import { NervousSystem } from './components/sections/NervousSystem';
import { Tasks } from './components/sections/Tasks';
import { Diploma } from './components/sections/Diploma';
import { Promotion } from './components/sections/Promotion';

export const Anatomy = memo(function Anatomy() {
  const referrer = useRef(document.referrer);
  const [params] = useSearchParams();
  const from = referrer.current || params.get('from') || UCHIRU_LINK;
  const [validFrom, setValidFrom] = useState(from);
  const [isLoaded, setIsLoaded] = useState(false);
  const [profile, setProfile] = useState<null | ProfileData | ApiErrorResponseData>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');

  useEffect(() => {
    if (from && URL_REG_EXP.test(from)) {
      setValidFrom(from);
    }
  }, [from]);

  useEffectOnMount(() => {
    async function fetchData() {
      if (process.env.NODE_ENV !== 'development') {
        try {
          const profileData = await fetchProfileData();
          setProfile(profileData);
          const isAuthorizedResponse = isAuthorizedProfile(profileData);
          setIsAuthorized(isAuthorizedResponse);

          if (isAuthorizedResponse) {
            const [hasPremium, paymentLinkData] = await Promise.all([
              fetchHasPremium('modern_anatomy'),
              fetchPaymentData(PRODUCT_NAME),
            ]);
            setIsPremium(hasPremium);
            if (!hasPremium && paymentLinkData.paymentPath) {
              setPaymentLink(paymentLinkData.paymentPath);
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log('error:', error);
        }
        setIsLoaded(true);
      } else {
        setIsLoaded(true);
      }
    }

    fetchData();
    document.title = TITLE;
  });

  if (!isLoaded) return null;

  // eslint-disable-next-line no-nested-ternary
  const typeLending = isAuthorized ? (isPremium ? 'bought' : 'notBought') : 'unauthorized';

  return (
    <ContextAnatomyProvider
      value={{
        type: typeLending,
        from: validFrom,
        profile,
        isAuthorized,
        isPremium,
        paymentLink,
      }}
    >
      <div className={styles.root}>
        <MainBannerSection />
        <AboutSection />
        <PhysicalActivitySection />
        <NervousSystem />
        <NutritionSection />
        <MetabolismSection />
        <ImmunitySection />
        <CourseContent />
        <VideoLessons />
        <Tasks />
        <Diploma />
        <Promotion />
      </div>
    </ContextAnatomyProvider>
  );
});
